import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { RAIL_COLLAPSED_WIDTH, RAIL_WIDTH } from '@components/lookup/SlicerRail';

/**
 * FilterRail - the queue's filters on the RIGHT, sliding out and in (his Care console page, img3 and
 * img6, 24 Sep 2026: "Clean this up with slicers on the right side (like the main portal) Slides
 * out/in"). The Lookup pages' rail and the main portal's measurements: 280 px open, 40 px closed to a
 * strip reading FILTERS with the count of active filters. The width animates between the two
 * (.filter-rail in index.css), so the rail slides rather than jumps; open or closed is remembered per
 * browser under its own key.
 *
 * The rail only NARROWS what is shown. It writes nothing, and the line saying how many rows the
 * slicers hide stays over the table (row 354), never in here, so a closed rail cannot hide the fact.
 */

export const FILTER_RAIL_WORDING = {
  title: 'Filters',
  active: (n: number) => `${n} active`,
  reset: 'Reset',
  collapse: 'Slide the filters in',
  expand: 'Slide the filters out',
  all: 'All',
} as const;

function readCollapsed(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false; // storage blocked: the rail opens, and the choice lasts this visit only
  }
}

export function FilterRail({
  storageKey,
  activeCount,
  onReset,
  canReset = activeCount > 0,
  scope,
  children,
}: {
  /** Where open or closed is remembered (one key per screen). */
  storageKey: string;
  activeCount: number;
  onReset: () => void;
  /** Whether Reset would change anything (a page that opens with a filter set says so here). */
  canReset?: boolean;
  /** The lines under the header: what is shown of what, and the campaign scope. */
  scope?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(storageKey));
  const set = (v: boolean) => {
    setCollapsed(v);
    try {
      localStorage.setItem(storageKey, v ? '1' : '0');
    } catch {
      /* storage blocked: session-only */
    }
  };

  return (
    <aside
      aria-label={FILTER_RAIL_WORDING.title}
      data-testid="filter-rail"
      data-collapsed={collapsed ? 'true' : 'false'}
      className={cn('filter-rail card card-flush', collapsed && 'collapsed')}
      style={{ width: collapsed ? RAIL_COLLAPSED_WIDTH : RAIL_WIDTH }}
    >
      {collapsed ? (
        <button
          type="button"
          className="filter-rail-spine"
          onClick={() => set(false)}
          title={FILTER_RAIL_WORDING.expand}
          aria-label={FILTER_RAIL_WORDING.expand}
          aria-expanded={false}
        >
          <span className="muted" style={{ fontSize: 11 }}>◀</span>
          {activeCount > 0 && (
            <span className="pill pill-primary" style={{ fontSize: 9, padding: '2px 5px' }}>{activeCount}</span>
          )}
          <span className="filter-rail-spine-word">{FILTER_RAIL_WORDING.title}</span>
        </button>
      ) : (
        <div className="filter-rail-body">
          <div className="filter-rail-head">
            <span className="filter-rail-title">{FILTER_RAIL_WORDING.title}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className={cn('small', 'filter-rail-count', activeCount > 0 && 'on')}>
                {FILTER_RAIL_WORDING.active(activeCount)}
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ padding: '2px 6px', fontSize: 9 }}
                disabled={!canReset}
                onClick={onReset}
              >
                {FILTER_RAIL_WORDING.reset}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{ padding: '2px 6px', fontSize: 10 }}
                onClick={() => set(true)}
                title={FILTER_RAIL_WORDING.collapse}
                aria-label={FILTER_RAIL_WORDING.collapse}
                aria-expanded
              >
                ▸
              </button>
            </span>
          </div>
          {scope && <div className="filter-rail-scope">{scope}</div>}
          <div className="filter-rail-slicers">{children}</div>
        </div>
      )}
    </aside>
  );
}

/**
 * One multi-select slicer in the rail, drawn exactly as the grid's own slicers are (grid-slicer /
 * grid-chip), so the page's filters and the table's filters read as one set. Nothing chosen means
 * all, which is what the All chip says; each value shows its count when the page has one.
 */
export function RailSlicer<K extends string>({
  label,
  options,
  selected,
  onChange,
  titleFor,
}: {
  label: string;
  options: { key: K; label: string; count?: number | undefined }[];
  selected: ReadonlySet<K>;
  onChange: (next: Set<K>) => void;
  titleFor?: (key: K) => string | undefined;
}): JSX.Element | null {
  if (options.length === 0) return null;
  return (
    <div className="grid-slicer" role="group" aria-label={label}>
      <span className="grid-slicer-label">{label}</span>
      <button
        type="button"
        className={selected.size ? 'grid-chip' : 'grid-chip on'}
        aria-pressed={selected.size === 0}
        onClick={() => onChange(new Set())}
      >
        {FILTER_RAIL_WORDING.all}
      </button>
      {options.map((o) => {
        const on = selected.has(o.key);
        return (
          <button
            key={o.key}
            type="button"
            className={on ? 'grid-chip on' : 'grid-chip'}
            aria-pressed={on}
            title={titleFor?.(o.key)}
            onClick={() => {
              const next = new Set(selected);
              if (on) next.delete(o.key);
              else next.add(o.key);
              onChange(next);
            }}
          >
            {o.label}
            {o.count !== undefined && <span className="n"> {o.count.toLocaleString()}</span>}
          </button>
        );
      })}
    </div>
  );
}
